import type { MascotConfig } from './mascot-catalog'
import styles from './DonorMascot.module.css'
import { mixMascotColor } from './mascot-colors'

export function MascotAccessory({
  accessory,
  accessoryColor,
}: Pick<MascotConfig, 'accessory' | 'accessoryColor'>) {
  if (accessory === 'none') return null
  const color =
    accessoryColor ??
    (accessory === 'collar' ? '#568c84' : accessory === 'bow' ? '#db94a7' : '#c86662')
  const shade = mixMascotColor(color, '#442b37', 0.3)
  const light = mixMascotColor(color, '#fff4dc', 0.42)
  return (
    <g strokeLinejoin="round" data-accessory={accessory}>
      {accessory === 'scarf' && (
        <g stroke={shade} strokeWidth="1.5">
          <path d="M86 155q34 17 68 0l-3 17q-31 17-62 0Z" fill={color} />
          <path d="m133 169 18 3-3 39-19-4Z" fill={color} />
          <path d="m132 196 16 3m-17-12 18 3" stroke={light} strokeWidth="4" />
          <path d="m130 207-1 6m7-5-1 6m7-5-1 6" stroke={shade} strokeWidth="2" />
        </g>
      )}
      {accessory === 'bowtie' && (
        <g stroke={shade} strokeWidth="1.5">
          <path d="M88 159q32 15 64 0" stroke={color} strokeWidth="6" fill="none" />
          <path d="m118 169-21-12v26l21-9m5-5 22-12v26l-22-9" fill={color} />
          <path d="m102 166 12 6m27-6-12 6" stroke={light} />
          <rect x="116" y="165" width="10" height="13" rx="4" fill={light} />
        </g>
      )}
      {accessory === 'pearls' && (
        <g>
          {[88, 99, 112, 126, 140, 151].map((x, i) => (
            <g key={x}>
              <circle
                cx={x}
                cy={160 + Math.sin((i / 5) * Math.PI) * 12}
                r="6"
                fill={color}
                stroke={shade}
              />
              <circle
                cx={x - 1.5}
                cy={158 + Math.sin((i / 5) * Math.PI) * 12}
                r="2"
                fill={light}
              />
            </g>
          ))}
        </g>
      )}
      {accessory === 'harness' && (
        <g stroke={shade} strokeWidth="1.4">
          <path d="M91 158q29 14 58 0l-6 35q-23 17-46 0Z" fill={color} />
          <path d="m96 166 24 28 24-28" fill="none" stroke={light} strokeWidth="4" />
          <path d="M120 182c-18-17-23 11 0 21 23-10 18-38 0-21Z" fill={light} />
        </g>
      )}
      {accessory === 'bandana' && (
        <>
          <path
            d="M86 153Q120 164 154 153L141 174 121 195 99 176Z"
            fill={color}
            stroke={shade}
            strokeWidth="1.5"
          />
          <path d="M90 157Q121 169 150 157L121 187Z" fill={light} />
          <path
            d="m103 170 18 20 18-20"
            fill="none"
            stroke="#ffe3be"
            strokeWidth="1.4"
            strokeDasharray="2 3"
          />
          <path d="M117 174c-5-6-10 2 4 9 14-7 9-15 4-9l-4 3Z" fill="#fff0d7" />
          <path d="m152 156 11 5-5 9-11-9" fill={shade} />
        </>
      )}
      {accessory === 'collar' && (
        <>
          <path
            d="M85 153Q120 173 155 153L153 163Q120 184 87 163Z"
            fill={color}
            stroke={shade}
            strokeWidth="1.5"
          />
          <path
            d="M90 159q30 16 60 0"
            fill="none"
            stroke={light}
            strokeWidth="2"
            strokeDasharray="2 4"
          />
          <g className={styles.charm}>
            <circle
              cx="120"
              cy="176"
              r="3.5"
              fill="none"
              stroke="#ae803c"
              strokeWidth="2"
            />
            <circle
              cx="120"
              cy="187"
              r="10"
              fill="#edc66f"
              stroke="#b88639"
              strokeWidth="1.5"
            />
            <circle cx="120" cy="186" r="7.5" fill="#ffe3a0" />
            <path d="M116 184c-4-4-6 3 4 7 10-4 8-11 4-7l-4 3Z" fill="#ba8941" />
            <path
              d="m115 180 3-1"
              stroke="#fff5d9"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </g>
        </>
      )}
      {accessory === 'bow' && (
        <>
          <path d="M86 157q34 14 68 0" fill="none" stroke={shade} strokeWidth="7" />
          <g className={styles.bow}>
            <path
              d="m111 169-12 23 15-5 7 6 4-23m4 0 14 21 2-10 12 1-20-17"
              fill={color}
              stroke={shade}
              strokeWidth="1"
            />
            <path
              d="M118 165Q86 140 86 163q-1 26 32 10m6-8q32-25 32-2 1 26-32 10"
              fill={color}
              stroke={shade}
              strokeWidth="1.5"
            />
            <path
              d="m94 159 19 9-19 7m54-16-19 9 19 7"
              fill="none"
              stroke={light}
              strokeWidth="2"
              strokeLinecap="round"
            />
            <rect x="115" y="160" width="13" height="17" rx="5" fill={shade} />
            <path d="M119 163v10" stroke={light} strokeWidth="2" strokeLinecap="round" />
          </g>
        </>
      )}
    </g>
  )
}

export function MascotToy({ toy, toyColor }: Pick<MascotConfig, 'toy' | 'toyColor'>) {
  if (toy === 'none') return null
  const color = toyColor ?? (toy === 'bone' ? '#fff4dc' : '#c97164')
  const shade = mixMascotColor(color, '#483044', 0.3)
  const light = mixMascotColor(color, '#fff9e7', 0.6)
  return (
    <g data-toy={toy}>
      <ellipse cx="193" cy="240" rx="24" ry="5" fill="#584632" opacity=".1" />
      {toy === 'rope_ring' && (
        <g transform="rotate(-16 193 224)" data-sheep-toy>
          <ellipse
            cx="193"
            cy="224"
            rx="16"
            ry="13"
            fill="none"
            stroke={shade}
            strokeWidth="9"
          />
          <ellipse
            cx="193"
            cy="224"
            rx="16"
            ry="13"
            fill="none"
            stroke={color}
            strokeWidth="7"
          />
          <ellipse
            cx="193"
            cy="224"
            rx="16"
            ry="13"
            fill="none"
            stroke={light}
            strokeWidth="6"
            strokeDasharray="2 5"
          />
          <path
            d="m204 231 10 8m-9-9 12 3"
            stroke={color}
            strokeWidth="3"
            strokeLinecap="round"
          />
        </g>
      )}
      {toy === 'flower_ball' && (
        <g data-sheep-toy>
          <circle cx="193" cy="223" r="18" fill={color} stroke={shade} />
          <path
            d="M179 213q8-5 15-4"
            stroke={light}
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
          {[0, 60, 120].map((angle) => (
            <ellipse
              key={angle}
              cx="196"
              cy="224"
              rx="3"
              ry="10"
              fill="#fff9e9"
              transform={`rotate(${angle} 196 224)`}
            />
          ))}
          <circle cx="196" cy="224" r="4" fill="#efc25d" />
        </g>
      )}
      {toy === 'plush_sheep' && (
        <g data-sheep-toy>
          <path
            d="M178 224q-8-6-2-12 2-7 9-4 5-8 11-2 8-3 10 5 8 2 5 9 5 8-3 12-1 7-10 5-7 6-12-1-9 3-10-5Z"
            fill={light}
            stroke={shade}
          />
          <path
            d="m180 232-1 7m19-5 1 6"
            stroke={shade}
            strokeWidth="6"
            strokeLinecap="round"
          />
          <ellipse cx="201" cy="217" rx="10" ry="12" fill="#f2c2b6" />
          <path
            d="m192 212-8 4m24-4 9 5"
            stroke={light}
            strokeWidth="6"
            strokeLinecap="round"
          />
          <path
            d="M195 210q4-5 10-1"
            stroke={light}
            strokeWidth="6"
            fill="none"
            strokeLinecap="round"
          />
          <circle cx="198" cy="217" r="1.4" fill="#634438" />
          <circle cx="206" cy="217" r="1.4" fill="#634438" />
          <path d="m200 222 2 2 2-2" fill="none" stroke="#925951" />
          <path d="m191 227 11 3-5 7Z" fill={color} />
        </g>
      )}
      {toy === 'yarn' && (
        <g>
          <circle cx="193" cy="223" r="18" fill={color} stroke={shade} />
          <path
            d="M179 213q23 1 28 19m-32-10q20-2 29 12m-18-27q-6 19 11 34m-19-10q18-13 30-11m-10 21q24 3 22-7"
            fill="none"
            stroke={light}
            strokeWidth="2"
          />
        </g>
      )}
      {toy === 'mouse' && (
        <g>
          <path d="M178 235q-8-28 13-27 22 0 22 27Z" fill={color} stroke={shade} />
          <circle cx="184" cy="211" r="8" fill={light} stroke={shade} />
          <circle cx="202" cy="211" r="8" fill={light} stroke={shade} />
          <circle cx="185" cy="226" r="2" />
          <circle cx="202" cy="226" r="2" />
          <path d="m190 230 4 4 4-4" fill={shade} />
          <path d="M211 235q22 2 10-15" fill="none" stroke={color} strokeWidth="3" />
        </g>
      )}
      {toy === 'duck' && (
        <g stroke={shade}>
          <path d="M175 222q9 10 20 1l13-7q13 20-7 24-24 5-26-18Z" fill={color} />
          <circle cx="186" cy="212" r="12" fill={color} />
          <path d="m176 211-9 5 11 4" fill="#efb860" />
          <circle cx="184" cy="210" r="2" fill="#3a3339" />
          <path d="M191 230q9 6 15-2" fill="none" stroke={light} strokeWidth="3" />
        </g>
      )}
      {toy === 'star' && (
        <g>
          <path
            d="m193 201 7 13 15 3-11 10 2 15-13-7-14 7 3-15-11-10 15-3Z"
            fill={color}
            stroke={shade}
            strokeWidth="1.5"
          />
          <path
            d="m185 222 3 0m10 0 3 0m-11 6q4 4 8 0"
            stroke={shade}
            strokeWidth="2"
            strokeLinecap="round"
            fill="none"
          />
          <path d="m191 208-3 10-10 2" stroke={light} strokeWidth="2" fill="none" />
        </g>
      )}
      {toy === 'fish' && (
        <g>
          <path d="M202 222l17-13v29l-17-12" fill={light} stroke={shade} />
          <ellipse cx="188" cy="224" rx="19" ry="12" fill={color} stroke={shade} />
          <circle cx="179" cy="222" r="2" fill="#38313b" />
          <path
            d="M190 216q7 8 0 16m7-14q5 6 0 12"
            stroke={light}
            strokeWidth="2"
            fill="none"
          />
        </g>
      )}
      {toy === 'ball' ? (
        <g>
          <circle
            cx="193"
            cy="224"
            r="18"
            fill={color}
            stroke={shade}
            strokeWidth="1.5"
          />
          <path
            d="M181 210q17 10 23 28m-28-13q23 0 30-11"
            fill="none"
            stroke={light}
            strokeWidth="5"
          />
          <path
            d="M182 215q2-4 6-4"
            fill="none"
            stroke="#fff0d6"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path d="M207 222q3 14-11 17" fill="none" stroke="#804d491f" strokeWidth="5" />
        </g>
      ) : toy === 'bone' ? (
        <g transform="rotate(-12 193 230)">
          <path
            d="M181 224c-7-10-15-7-12 1-8 4-4 14 6 9h32c8 7 16-2 9-8 4-9-6-13-12-2Z"
            fill={color}
            stroke={shade}
            strokeWidth="1.5"
          />
          <path d="M180 228h23" stroke="#fffdf3" strokeWidth="3" strokeLinecap="round" />
          <path d="M177 233h28" stroke="#e7d4b4" strokeWidth="2" strokeLinecap="round" />
        </g>
      ) : null}
    </g>
  )
}

export function MascotDirt({ amount, face = false }: { amount: number; face?: boolean }) {
  if (!amount) return null
  return (
    <g className={styles.dirt} fill="#81634e" opacity={amount * 0.85}>
      {face ? (
        <>
          <path d="M55 118q8-7 15 1t-1 9q-13 3-14-10" opacity=".32" />
          <path d="m162 136 10-6 6 6-5 7-11-3Z" opacity={amount * 0.5} />
          <circle cx="61" cy="113" r="2.2" opacity=".45" />
          <circle cx="176" cy="126" r="2" opacity={amount} />
          <path
            d="m147 63 8 3m-10 2 7 3"
            stroke="#6e624c"
            strokeWidth="2"
            strokeLinecap="round"
            opacity={amount}
          />
        </>
      ) : (
        <>
          <path d="M97 199q8-9 17-1l5 9q-11 11-22-1Z" opacity=".36" />
          <path d="m133 185 10-3 9 11-9 8-8-4Z" opacity={amount * 0.5} />
          <ellipse cx="81" cy="224" rx="13" ry="5" opacity=".5" />
          <ellipse cx="149" cy="229" rx="11" ry="4" opacity={amount * 0.8} />
          <circle cx="106" cy="188" r="2.5" opacity=".4" />
          <circle cx="130" cy="213" r="3" opacity={amount * 0.6} />
        </>
      )}
    </g>
  )
}
